import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Button,
  ButtonGroup,
  Chip,
  useMediaQuery,
} from "@mui/material";
import {
  CalendarMonth,
  Today,
  DateRange,
  TextSnippet,
  Close,
} from "@mui/icons-material";
import MonthlyReports from "./monthlyReports";
import WeeklyReports from "./weeklyReports";
import DailyReports from "./dailyReports";
import ReportGroups from "./reportGroups";
import axios from "../../utils/axiosInstance";
import { useNavigate, useLocation } from "react-router-dom";

const Reports = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  // Get filter info from navigation state
  const filterState = location.state || {};
  const {
    deviceIds,
    filterApplied,
    name: filterName,
    type: filterType,
  } = filterState;

  // Determine active view from URL path
  const activeView = useMemo(() => {
    const path = location.pathname;
    if (path.includes("/monthly")) return "monthlyReports";
    if (path.includes("/weekly")) return "weeklyReports";
    if (path.includes("/daily")) return "dailyReports";
    if (path.includes("/report-group")) return "reportGroups";
    return "monthlyReports";
  }, [location.pathname]);

  const [units, setUnits] = useState([]);
  const [loadingUnitSearchData, setLoadingUnitSearchData] = useState(true);

  const navigationItems = [
    {
      id: "monthlyReports",
      label: "Monthly Reports",
      icon: <CalendarMonth />,
      component: MonthlyReports,
      color: "#1565c0",
      path: "../reports/monthly",
    },
    {
      id: "weeklyReports",
      label: "Weekly Reports",
      icon: <DateRange />,
      component: WeeklyReports,
      color: "#1565c0",
      path: "../reports/weekly",
    },
    {
      id: "dailyReports",
      label: "Daily Reports",
      icon: <Today />,
      component: DailyReports,
      color: "#1565c0",
      path: "../reports/daily",
    },
    {
      id: "reportGroups",
      label: "Report Groups",
      icon: <TextSnippet />,
      component: ReportGroups,
      color: "#1565c0",
      path: "../reports/report-group",
    },
  ];

  const activeItem = navigationItems.find((item) => item.id === activeView);
  const ActiveComponent = activeItem?.component || MonthlyReports;

  const handleViewChange = (item) => {
    // Preserve filter state when changing views
    navigate(item.path, {
      state: filterApplied ? filterState : undefined,
    });
  };

  const handleClearFilter = () => {
    // Navigate to current view without filter state
    const currentPath = location.pathname;
    navigate(currentPath, { replace: true, state: {} });
  };

  const fetchUnits = async () => {
    try {
      setLoadingUnitSearchData(true);
      let response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/devices?companyId=${process.env.REACT_APP_COMPANY_ID}`
      );
      const data = response.data;

      // Filter units if deviceIds are provided
      let processedUnits = data.map((unit) => ({
        id: unit.id,
        name: unit.name,
      }));

      // Apply filter if deviceIds are present
      if (filterApplied && deviceIds && deviceIds.length > 0) {
        processedUnits = processedUnits.filter((unit) =>
          deviceIds.includes(unit.id)
        );
      }

      setUnits(processedUnits);
    } catch (err) {
      console.error("Error fetching devices:", err);
    } finally {
      setLoadingUnitSearchData(false);
    }
  };

  useEffect(() => {
    fetchUnits();
  }, [deviceIds, filterApplied]); // Re-fetch when filter changes

  return (
    <Box sx={{ bgcolor: "#1e293b", minHeight: "90vh" }}>
      {/* Navigation Bar */}
      <AppBar
        position="static"
        sx={{ backgroundColor: "#111827", boxShadow: 1 }}
      >
        <Toolbar sx={{ justifyContent: "space-between", py: 1 }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {filterApplied && filterName && (
              <Chip
                label={`Filtered: ${filterName.replace(
                  /^prc\s*-\s*/i,
                  ""
                )} (${filterType})`}
                onDelete={handleClearFilter}
                deleteIcon={<Close />}
                sx={{
                  backgroundColor: "#18b8e0ff",
                  color: "white",
                  fontWeight: 600,
                  "& .MuiChip-deleteIcon": {
                    color: "white",
                    "&:hover": {
                      color: "#f87171",
                    },
                  },
                }}
              />
            )}
            {filterApplied && deviceIds && deviceIds.length > 0 && (
              <Chip
                label={`${deviceIds.length} device${
                  deviceIds.length !== 1 ? "s" : ""
                }`}
                size="small"
                sx={{
                  backgroundColor: "#374151",
                  color: "#9ca3af",
                  fontWeight: 500,
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            <ButtonGroup
              orientation={isSmallScreen ? "vertical" : "horizontal"}
              fullWidth={isSmallScreen}
            >
              {navigationItems.map((item) => (
                <Button
                  key={item.id}
                  startIcon={item.icon}
                  onClick={() => handleViewChange(item)}
                  sx={{
                    borderRadius: 0,
                    px: isSmallScreen ? 2 : 3,
                    py: 1,
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: isSmallScreen ? "13px" : "14px",
                    minWidth: isSmallScreen ? "auto" : "160px",
                    bgcolor: activeView === item.id ? item.color : "#475467ff",
                    color: "white",
                    border: "1px solid #0ba6ffff",
                    "&:hover": {
                      bgcolor: activeView === item.id ? item.color : "#4b5563",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </ButtonGroup>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Content Area */}
      <Box sx={{ position: "relative", minHeight: "86vh" }}>
        <ActiveComponent
          units={units}
          loadingUnitSearchData={loadingUnitSearchData}
          filterApplied={filterApplied}
          filterInfo={filterState}
        />
      </Box>
    </Box>
  );
};

export default Reports;
